import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import ChatHistoryPanel from './ChatHistoryPanel';

const CITATION_PATTERN = /\[(normativa:[^\]]+)\]/g;

const sourceKey = (source) => source?.chunk_id
    || `${source?.normativa_id || 'unknown'}:${source?.title || ''}:${source?.article || ''}:${source?.page || ''}:${source?.url || ''}`;

const mergeSources = (current, incoming) => {
    const byKey = new Map((current || []).map(source => [sourceKey(source), source]));
    (incoming || []).filter(Boolean).forEach(source => {
        byKey.set(sourceKey(source), source);
    });
    return Array.from(byKey.values());
};

const CitationMarkdown = ({ text, sources = [], citationOrder = [], onOpenCitation }) => {
    const sourceById = new Map(
        sources.filter(source => source?.chunk_id).map(source => [String(source.chunk_id), source]),
    );
    const citationNumberById = new Map(
        citationOrder.map((citationId, index) => [String(citationId), index + 1]),
    );

    const normalizedText = String(text || '').replace(CITATION_PATTERN, (_, citationId) => {
        const source = sourceById.get(citationId);
        if (!source) return '[?](#citation-missing)';
        const citationNumber = citationNumberById.get(citationId)
            || sources.filter(item => item?.chunk_id).findIndex(item => String(item.chunk_id) === citationId) + 1;
        return `[${citationNumber}](#citation-${encodeURIComponent(citationId)})`;
    });

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({ href, children, ...props }) => {
                    if (href?.startsWith('#citation-')) {
                        const citationId = decodeURIComponent(href.slice('#citation-'.length));
                        const source = sourceById.get(citationId);
                        return (
                            <button
                                type="button"
                                className="citation-link"
                                onClick={() => source && onOpenCitation(source)}
                                aria-label={source ? `Ver fuente ${citationNumberById.get(citationId)}` : 'Fuente no disponible'}
                            >
                                {children}
                            </button>
                        );
                    }
                    if (href === '#citation-missing') {
                        return (
                            <span className="citation-link citation-link-missing" title="Fuente no disponible">
                                {children}
                            </span>
                        );
                    }
                    return (
                        <a href={href} target="_blank" rel="noreferrer" {...props}>
                            {children}
                        </a>
                    );
                },
            }}
        >
            {normalizedText}
        </ReactMarkdown>
    );
};

const ChatWindow = ({ title, apiEndpoint }) => {
    const { token } = useAuth();
    const { t } = useLanguage();
    const text_regulations = t('chat.welcome.regulations');
    const text_concursos = t('chat.welcome.concursos');
    const initialBot = apiEndpoint.endsWith('/regulations') ? text_regulations : text_concursos;
    const [messages, setMessages] = useState(() => [
        { id: 'welcome', sender: 'bot', text: initialBot, citations: [] }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false); // Nuevo: indica si estamos recibiendo chunks
    // const [streamStartTime, setStreamStartTime] = useState(null); // TODO: Implementar medición de tiempo de respuesta
    const [progress, setProgress] = useState([]); // lista de eventos de progreso
    const [selectedNormativaId, setSelectedNormativaId] = useState(''); // '' = automático
    const [sources, setSources] = useState([]);
    const [normativas, setNormativas] = useState([]);
    const [citationValidation, setCitationValidation] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [selectedCitation, setSelectedCitation] = useState(null);
    const [isEvidenceOpen, setIsEvidenceOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
    // Historial persistente
    const [sessionId, setSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [sessionTitle, setSessionTitle] = useState('');
    const messageListRef = useRef(null);
    const sourcesRef = useRef([]);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        const list = messageListRef.current;
        if (!list) return;
        const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
        if (distanceFromBottom < 160) {
            list.scrollTop = list.scrollHeight;
        }
    });

    // Reset completo al cambiar de endpoint (cambio de módulo)
    useEffect(() => {
        const botText = apiEndpoint.endsWith('/regulations') ? text_regulations : text_concursos;
        setMessages([{ id: 'welcome', sender: 'bot', text: botText, citations: [] }]);
        setInput('');
        setProgress([]);
        setIsLoading(false);
        setIsStreaming(false); // Nuevo
        // setStreamStartTime(null);
        setSources([]);
        sourcesRef.current = [];
        setNormativas([]);
        setCitationValidation(null);
        setWarnings([]);
        setSelectedCitation(null);
        setIsEvidenceOpen(false);
        setIsHistoryOpen(false);
        setSelectedNormativaId('');
        // setNormativas([]);
        setSessionId(null);
        setSessions([]);
        setSessionTitle('');
    }, [apiEndpoint, text_concursos, text_regulations]);

    // Cargar normativas y sesiones en paralelo
    useEffect(() => {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const moduleId = apiEndpoint.endsWith('/regulations') ? 1 : 7;
        
        (async () => {
            try {
                // Ejecutar fetches en paralelo donde sea posible
                const promises = [];
                
                if (apiEndpoint.endsWith('/regulations')) {
                    promises.push(
                        fetch(`${API_BASE}/chat/normativas`, {
                            headers: { Authorization: `Bearer ${token}` },
                        })
                            .then(res => res.ok ? res.json() : [])
                            .then(data => setNormativas(Array.isArray(data) ? data : []))
                            .catch(e => console.error('No se pudieron cargar las normativas:', e))
                    );
                }
                
                if (token) {
                    promises.push(
                        fetch(`${API_BASE}/history/list/${moduleId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        })
                            .then(res => res.ok ? res.json() : [])
                            .then(data => setSessions(Array.isArray(data) ? data : []))
                            .catch(e => console.error('No se pudo cargar el historial:', e))
                    );
                }
                
                // Esperar que todas las promesas se resuelvan en paralelo
                await Promise.all(promises);
            } catch (e) {
                console.error('Error cargando datos:', e);
            }
        })();
    }, [apiEndpoint, token]);

    const startNewSession = useCallback(async () => {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const moduleId = apiEndpoint.endsWith('/regulations') ? 1 : 7;
        const botText = apiEndpoint.endsWith('/regulations') ? text_regulations : text_concursos;
        setMessages([{ id: 'welcome', sender: 'bot', text: botText, citations: [] }]);
        setSources([]);
        sourcesRef.current = [];
        setProgress([]);
        setWarnings([]);
        setCitationValidation(null);
        setSelectedCitation(null);
        setIsEvidenceOpen(false);
        setIsHistoryOpen(false);
        const newConversationTitle = t('chat.history.newConversation');
        setSessionTitle(newConversationTitle);
        try {
            const res = await fetch(`${API_BASE}/history/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ module_id: moduleId, title: newConversationTitle, messages: [{ role: 'ai', content: botText }] }),
            });
            if (res.ok) {
                const h = await res.json();
                setSessionId(h.id);
                // refrescar lista
                const res2 = await fetch(`${API_BASE}/history/list/${moduleId}`, { headers: { Authorization: `Bearer ${token}` } });
                if (res2.ok) setSessions(await res2.json());
            }
        } catch (e) {
            console.error('No se pudo crear la sesión:', e);
        }
    }, [apiEndpoint, t, text_concursos, text_regulations, token]);

    const loadSession = useCallback(async (id) => {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        try {
            const res = await fetch(`${API_BASE}/history/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) return;
            const h = await res.json();
            setSessionId(h.id);
            const restoredSources = (h.messages || []).flatMap(message => message.citations || []);
            sourcesRef.current = mergeSources([], restoredSources);
            setSources(sourcesRef.current);
            setCitationValidation(null);
            setSelectedCitation(null);
            setSessionTitle(h.title || 'Conversación');
            // Mapear mensajes persistidos a UI
            const mapped = (h.messages || []).map((m, index) => ({
                id: m.id || `${m.role}-${index}`,
                sender: m.role === 'human' ? 'user' : 'bot',
                text: m.content,
                citations: m.citations || [],
            }));
            setMessages(mapped.length ? mapped : [{ id: 'welcome', sender: 'bot', text: initialBot, citations: [] }]);
            setIsHistoryOpen(false);
        } catch (e) {
            console.error('No se pudo cargar la sesión:', e);
        }
    }, [initialBot, token]);

    const renameSession = useCallback(async (id, newTitle) => {
        const targetId = id || sessionId;
        if (!targetId) return;
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        try {
            const res = await fetch(`${API_BASE}/history/${targetId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ title: newTitle }),
            });
            if (res.ok) {
                if (targetId === sessionId) setSessionTitle(newTitle);
                // refresh list
                const moduleId = apiEndpoint.endsWith('/regulations') ? 1 : 7;
                const res2 = await fetch(`${API_BASE}/history/list/${moduleId}`, { headers: { Authorization: `Bearer ${token}` } });
                if (res2.ok) setSessions(await res2.json());
            }
        } catch (e) {
            console.error('No se pudo renombrar la sesión:', e);
        }
    }, [apiEndpoint, sessionId, token]);

    const deleteSession = useCallback(async (id) => {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const moduleId = apiEndpoint.endsWith('/regulations') ? 1 : 7;
        const target = sessions.find(session => session.id === id);
        const targetTitle = target?.title || t('chat.history.defaultTitle');
        if (!window.confirm(`${t('chat.history.confirmDelete')}\n\n${targetTitle}`)) return;
        try {
            await fetch(`${API_BASE}/history/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            // limpiar si eliminamos la activa
            if (sessionId === id) {
                setSessionId(null);
                const botText = apiEndpoint.endsWith('/regulations') ? text_regulations : text_concursos;
                setMessages([{ id: 'welcome', sender: 'bot', text: botText, citations: [] }]);
                setSessionTitle('');
                setSources([]);
                sourcesRef.current = [];
                setSelectedCitation(null);
                setIsEvidenceOpen(false);
            }
            const res2 = await fetch(`${API_BASE}/history/list/${moduleId}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res2.ok) setSessions(await res2.json());
        } catch (e) {
            console.error('No se pudo eliminar la sesión:', e);
        }
    }, [apiEndpoint, sessionId, sessions, t, text_concursos, text_regulations, token]);

    const updateSourceCatalog = (incoming) => {
        sourcesRef.current = mergeSources(sourcesRef.current, incoming);
        setSources(sourcesRef.current);
        return sourcesRef.current;
    };

    const handleOpenCitation = (source) => {
        setSelectedCitation(source);
        setIsEvidenceOpen(true);
    };

    const handleCopyMessage = async (text, index) => {
        try {
            await navigator.clipboard.writeText(String(text || ''));
            setCopiedMessageIndex(index);
            window.setTimeout(() => setCopiedMessageIndex(null), 1800);
        } catch (error) {
            console.error('No se pudo copiar la respuesta:', error);
        }
    };

    const handleStop = () => {
        abortControllerRef.current?.abort();
    };

    const handlePolicySend = async (queryOverride = null) => {
        const draftText = queryOverride ?? input;
        if (!draftText.trim() || isLoading || isStreaming || !token) return;

        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const userText = draftText.trim();
        const turnId = Date.now();
        const userMessage = { id: `user-${turnId}`, sender: 'user', text: userText };
        setMessages(prev => [...prev, userMessage, { id: `bot-${turnId}`, sender: 'bot', text: '', citations: [] }]);
        setInput('');
        setProgress([]);
        setSources([]);
        sourcesRef.current = [];
        setWarnings([]);
        setCitationValidation(null);
        setSelectedCitation(null);
        setIsEvidenceOpen(false);
        setIsLoading(true);
        setIsStreaming(false);
        abortControllerRef.current = new AbortController();

        try {
            const body = {
                query: userText,
                session_id: sessionId || null,
            };
            if (selectedNormativaId) body.normativa_id = Number(selectedNormativaId);

            const response = await fetch(`${API_BASE}/chat/regulations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
                signal: abortControllerRef.current.signal,
            });
            if (!response.ok) {
                const detail = await response.json().catch(() => ({}));
                throw new Error(detail.detail || `HTTP ${response.status}`);
            }
            if (!response.body) throw new Error('La respuesta no contiene un stream');

            const responseSessionId = response.headers.get('X-Session-ID');
            if (responseSessionId) setSessionId(Number(responseSessionId));
            setIsStreaming(true);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let botAccum = '';

            const applyEvent = (eventName, payload) => {
                if (eventName === 'progress') {
                    if (payload?.message) setProgress(prev => [...prev, payload.message]);
                } else if (eventName === 'warning') {
                    if (payload?.validation) setCitationValidation(payload.validation);
                    if (payload?.message) setWarnings(prev => [...prev, payload.message]);
                } else if (eventName === 'source') {
                    const incoming = Array.isArray(payload?.sources) ? payload.sources : [];
                    updateSourceCatalog(incoming);
                } else if (eventName === 'delta') {
                    const text = payload?.text || '';
                    botAccum += text;
                    setMessages(prev => {
                        const last = prev[prev.length - 1] || { sender: 'bot', text: '' };
                        return [...prev.slice(0, -1), { ...last, text: last.text + text }];
                    });
                } else if (eventName === 'replace') {
                    botAccum = payload?.text || '';
                    setMessages(prev => {
                        const last = prev[prev.length - 1] || { sender: 'bot', text: '' };
                        return [...prev.slice(0, -1), { ...last, text: botAccum }];
                    });
                } else if (eventName === 'done') {
                    const finalSources = updateSourceCatalog(payload?.sources || []);
                    setCitationValidation(payload?.citation_validation || null);
                    setMessages(prev => {
                        const last = prev[prev.length - 1] || { sender: 'bot', text: '' };
                        return [...prev.slice(0, -1), { ...last, citations: finalSources }];
                    });
                } else if (eventName === 'error') {
                    throw new Error(payload?.message || 'Error generando la respuesta');
                }
            };

            const consumeFrame = frame => {
                const lines = frame.split(/\r?\n/);
                const eventName = lines.find(line => line.startsWith('event:'))?.slice(6).trim() || 'message';
                const data = lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('\n');
                if (!data) return;
                applyEvent(eventName, JSON.parse(data));
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const frames = buffer.split(/\r?\n\r?\n/);
                buffer = frames.pop() || '';
                frames.filter(Boolean).forEach(frame => { consumeFrame(frame); });
            }
            buffer += decoder.decode();
            if (buffer.trim()) consumeFrame(buffer);
        } catch (error) {
            console.error('Error en Policy Navigator:', error);
            const wasStopped = error?.name === 'AbortError';
            const isConnectionError = !wasStopped && (
                error?.message === 'Failed to fetch'
                || error?.message === 'NetworkError'
            );
            setMessages(prev => {
                const last = prev[prev.length - 1] || { sender: 'bot', text: '' };
                const message = wasStopped
                    ? t('chat.errors.responseStopped')
                    : isConnectionError
                    ? t('chat.errors.connectionInterrupted')
                    : (error.message || t('chat.errors.sendFailed'));
                const separator = last.text ? '\n\n' : '';
                return [...prev.slice(0, -1), {
                    ...last,
                    text: `${last.text || ''}${separator}${message}`,
                    error: true,
                    retryText: userText,
                }];
            });
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
            const moduleId = 1;
            try {
                const res = await fetch(`${API_BASE}/history/list/${moduleId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setSessions(await res.json());
            } catch (error) {
                console.error('No se pudo refrescar el historial:', error);
            }
            abortControllerRef.current = null;
        }
    };

    const handleSend = async () => {
        if (apiEndpoint.endsWith('/regulations')) {
            return handlePolicySend();
        }
        if (!input.trim() || isLoading || isStreaming) return;

        const turnId = Date.now();
        const userMessage = { id: `user-${turnId}`, sender: 'user', text: input };
        setMessages(prev => [...prev, userMessage, { id: `bot-${turnId}`, sender: 'bot', text: '' }]);
        setInput('');
        setIsLoading(true);
        setIsStreaming(false); // Aún no llegan chunks
        // setStreamStartTime(Date.now()); // TODO: Implementar medición de tiempo
        // setSourceInfo(null); // TODO: Implementar visualización de fuentes
        // Variables compartidas entre try/finally
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const moduleId = apiEndpoint.endsWith('/regulations') ? 1 : 7;
    let createdThisTurn = false;
    let botAccum = '';
    // Importante: capturar un id de sesión local para usar durante todo el flujo
    let currentSessionId = sessionId;

    try {
            // Si no hay sesión y hay token, crearla con historial previo + mensaje del usuario
            if (!currentSessionId && token) {
                const preMsgs = [...messages, userMessage]
                    .filter(m => m.text && m.text.trim().length > 0)
                    .map(m => ({ role: m.sender === 'user' ? 'human' : 'ai', content: m.text }));
                try {
                    const resCreate = await fetch(`${API_BASE}/history/create`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ module_id: moduleId, title: sessionTitle || 'Conversación', messages: preMsgs }),
                    });
                    if (resCreate.ok) {
                        const h = await resCreate.json();
                        setSessionId(h.id);
                        currentSessionId = h.id; // usar inmediatamente en esta invocación
                        createdThisTurn = true;
                        const res2 = await fetch(`${API_BASE}/history/list/${moduleId}`, { headers: { Authorization: `Bearer ${token}` } });
                        if (res2.ok) setSessions(await res2.json());
                    }
                } catch (e) {
                    console.error('No se pudo crear la sesión previa:', e);
                }
            }
            // Construir historial para ambos endpoints (journals y regulations)
            const history = [...messages, userMessage]
                .filter(m => m.text && m.text.trim().length > 0)
                .map(m => ({
                    role: m.sender === 'user' ? 'human' : 'ai',
                    content: m.text,
                }));

            const body = { query: userMessage.text, history };
            if (apiEndpoint.endsWith('/regulations') && selectedNormativaId) {
                body.normativa_id = Number(selectedNormativaId);
            }
            const response = await fetch(`${API_BASE}${apiEndpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            if (!response.body) return;

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let firstChunkReceived = false; // Para detectar el primer chunk

            setProgress([]); // reiniciar progreso
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                // Marcar que comenzó el streaming al recibir el primer chunk
                if (!firstChunkReceived) {
                    firstChunkReceived = true;
                    setIsStreaming(true);
                    setIsLoading(false); // Ya no está "cargando", está "streaming"
                }
                
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                // Procesar sólo líneas completas; conservar el resto en buffer
                const lines = buffer.split(/\r?\n/);
                const hasTrailingNewline = /\r?\n$/.test(buffer);
                const completeLines = hasTrailingNewline ? lines : lines.slice(0, -1);

                for (const line of completeLines) {
                    if (line.startsWith('§PROGRESS§')) {
                        const msg = line.replace('§PROGRESS§', '');
                        setProgress(prev => [...prev, msg]);
                    } else if (line.startsWith('§SOURCE§')) {
                        // TODO: Descomentar cuando se implemente visualización de fuentes
                        // const payload = line.replace('§SOURCE§', '').trim();
                        // const [title, url] = payload.split('|').map(s => s && s.trim());
                        // if (title || url) setSourceInfo({ title, url });
                        continue;
                    } else if (line.match(/§NORMATIVA_ID:\d+§/)) {
                        // Marcador interno para rastreo de contexto - no mostrar al usuario
                        continue;
                    } else {
                        setMessages(prev => {
                            const lastMessage = prev[prev.length - 1];
                            const updatedLastMessage = { ...lastMessage, text: lastMessage.text + line + '\n' };
                            return [...prev.slice(0, -1), updatedLastMessage];
                        });
                        botAccum += line + '\n';
                    }
                }

                buffer = hasTrailingNewline ? '' : lines[lines.length - 1] || '';
            }

            // Procesar cualquier resto sin salto final (última línea incompleta)
            if (buffer.length > 0) {
                if (buffer.startsWith('§PROGRESS§')) {
                    const msg = buffer.replace('§PROGRESS§', '');
                    setProgress(prev => [...prev, msg]);
                } else if (buffer.startsWith('§SOURCE§')) {
                    // TODO: Descomentar cuando se implemente visualización de fuentes
                    // const payload = buffer.replace('§SOURCE§', '').trim();
                    // const [title, url] = payload.split('|').map(s => s && s.trim());
                    // if (title || url) setSourceInfo({ title, url });
                } else if (buffer.match(/§NORMATIVA_ID:\d+§/)) {
                    // Marcador interno para rastreo de contexto - no mostrar al usuario
                    // No hacer nada, solo ignorar
                } else {
                    setMessages(prev => {
                        const lastMessage = prev[prev.length - 1];
                        const updatedLastMessage = { ...lastMessage, text: lastMessage.text + buffer };
                        return [...prev.slice(0, -1), updatedLastMessage];
                    });
                    botAccum += buffer;
                }
            }

        } catch (error) {
            console.error("Error fetching streaming response:", error);
            setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                const updatedLastMessage = { ...lastMessage, text: t('chat.errors.sendFailed') };
                return [...prev.slice(0, -1), updatedLastMessage];
            });
        } finally {
            setIsLoading(false);
            setIsStreaming(false); // Finalizar streaming
            // setStreamStartTime(null); // TODO: Descomentar cuando se implemente medición de tiempo
            // Persistir: agregar respuesta del bot (y el mensaje del usuario si la sesión ya existía)
            try {
                if (token && (currentSessionId || botAccum)) {
                    // agregar solo la respuesta del bot si la sesión se creó en este turno;
                    // si ya existía, agregar usuario + bot
                    if (currentSessionId) {
                        await fetch(`${API_BASE}/history/${currentSessionId}/append`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                            body: JSON.stringify({ new_messages: createdThisTurn ? [
                                { role: 'ai', content: botAccum || '' },
                            ] : [
                                { role: 'human', content: userMessage.text },
                                { role: 'ai', content: botAccum || '' },
                            ] }),
                        });
                        const res2 = await fetch(`${API_BASE}/history/list/${moduleId}`, { headers: { Authorization: `Bearer ${token}` } });
                        if (res2.ok) setSessions(await res2.json());
                    }
                }
            } catch (e) {
                console.error('No se pudo persistir el historial:', e);
            }
        }
    };

    const citationOrder = Array.from(new Set(
        messages.flatMap(message => Array.from(String(message.text || '').matchAll(CITATION_PATTERN), match => match[1])),
    ));
    const citationNumbers = new Map(citationOrder.map((citationId, index) => [citationId, index + 1]));

    return (
        <div className="policy-chat-page">
            <div className="policy-chat-layout">
                <div className={`policy-history-column ${isHistoryOpen ? 'is-open' : ''}`}>
                    <ChatHistoryPanel
                        sessions={sessions}
                        selectedId={sessionId}
                        onSelect={loadSession}
                        onNew={startNewSession}
                        onRename={renameSession}
                        onDelete={deleteSession}
                        onClose={() => setIsHistoryOpen(false)}
                        loading={isLoading || isStreaming}
                    />
                </div>
                {isHistoryOpen && (
                    <button
                        type="button"
                        className="policy-history-backdrop"
                        onClick={() => setIsHistoryOpen(false)}
                        aria-label={t('chat.history.close')}
                    />
                )}
                <div className="policy-chat-column">
                    <section className="card shadow-sm border-0 policy-chat-card">
                        <header className="chat-header">
                            <div className="chat-heading">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary chat-history-toggle d-md-none"
                                    onClick={() => setIsHistoryOpen(true)}
                                    aria-label={t('chat.history.open')}
                                >
                                    <i className="bi bi-clock-history" aria-hidden="true" />
                                </button>
                                <div>
                                    <h2 className="h5 mb-1 text-primary fw-semibold">{title}</h2>
                                    {sessionTitle && <p className="chat-session-title mb-0">{sessionTitle}</p>}
                                </div>
                            </div>
                            {apiEndpoint.endsWith('/regulations') && sources.length > 0 && (
                                <button
                                    type="button"
                                    className="evidence-summary"
                                    onClick={() => setIsEvidenceOpen(open => !open)}
                                    aria-expanded={isEvidenceOpen}
                                >
                                    <i className="bi bi-journal-check" aria-hidden="true" />
                                    <span>{t('chat.sources.count', { count: sources.length })}</span>
                                    <i className={`bi bi-chevron-${isEvidenceOpen ? 'up' : 'down'}`} aria-hidden="true" />
                                </button>
                            )}
                        </header>

                        {apiEndpoint.endsWith('/regulations') && (
                            <div className="policy-context-bar">
                                <label htmlFor="policy-normativa-select" className="form-label mb-0">
                                    {t('chat.sources.contextLabel')}
                                </label>
                                <select
                                    id="policy-normativa-select"
                                    name="normativa_id"
                                    className="form-select form-select-sm"
                                    value={selectedNormativaId}
                                    onChange={event => setSelectedNormativaId(event.target.value)}
                                    disabled={isLoading || isStreaming}
                                >
                                    <option value="">{t('chat.sources.automatic')}</option>
                                    {normativas.map(normativa => (
                                        <option key={normativa.id} value={normativa.id}>
                                            {normativa.titulo}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {isEvidenceOpen && sources.length > 0 && (
                            <section className="evidence-panel" aria-labelledby="evidence-panel-title">
                                <div className="evidence-panel-heading">
                                    <div>
                                        <h3 id="evidence-panel-title" className="h6 mb-1">{t('chat.sources.title')}</h3>
                                        <p className="small text-muted mb-0">{t('chat.sources.description')}</p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-muted"
                                        onClick={() => setIsEvidenceOpen(false)}
                                        aria-label={t('common.close')}
                                    >
                                        <i className="bi bi-x-lg" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="evidence-grid">
                                    {sources.map(source => (
                                        <article
                                            key={sourceKey(source)}
                                            className={`evidence-card ${selectedCitation && sourceKey(selectedCitation) === sourceKey(source) ? 'is-selected' : ''}`}
                                        >
                                            <div className="evidence-card-number">
                                                {source.chunk_id && citationNumbers.has(String(source.chunk_id))
                                                    ? citationNumbers.get(String(source.chunk_id))
                                                    : '•'}
                                            </div>
                                            <div className="evidence-card-content">
                                                <h4 className="evidence-card-title">{source.title || t('chat.sources.unknown')}</h4>
                                                <div className="evidence-card-meta">
                                                    {source.article && <span>{t('chat.sources.article', { article: source.article })}</span>}
                                                    {source.page && <span>{t('chat.sources.page', { page: source.page })}</span>}
                                                </div>
                                                {source.quote && <p className="evidence-card-quote">“{source.quote}”</p>}
                                                <div className="evidence-card-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => setSelectedCitation(source)}
                                                    >
                                                        {t('chat.sources.focus')}
                                                    </button>
                                                    {source.url && (
                                                        <a className="btn btn-sm btn-link" href={source.url} target="_blank" rel="noreferrer">
                                                            {t('chat.sources.open')}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </section>
                        )}

                        {(isLoading || isStreaming) && (
                            <div className="progress-panel" role="status" aria-live="polite">
                                <div className="progress-icon progress-spinner" aria-hidden="true" />
                                <div className="progress-content">
                                    <div className="progress-title">
                                        {isStreaming ? t('chat.streamingResponse') : t('chat.thinking')}
                                    </div>
                                    {progress.length > 0 && (
                                        <ul className="mb-0 small">
                                            {Array.from(new Set(progress)).map(message => (
                                                <li key={message}>{message}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {isLoading && !isStreaming && (
                                        <div className="small text-muted mt-1">{t('common.loading')}</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {warnings.length > 0 && (
                            <div className="chat-warning" role="status">
                                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                                <span>{warnings[warnings.length - 1]}</span>
                            </div>
                        )}

                        {citationValidation?.invalid?.length > 0 && (
                            <div className="chat-warning" role="status">
                                <i className="bi bi-shield-exclamation" aria-hidden="true" />
                                <span>{t('chat.sources.validationWarning')}</span>
                            </div>
                        )}

                        <div className="message-list" ref={messageListRef} role="log" aria-label={t('chat.messagesLabel')}>
                            {messages.map((msg, index) => {
                                const isUser = msg.sender === 'user';
                                const isLastBotMessage = !isUser && index === messages.length - 1;
                                const showTypingIndicator = isLastBotMessage && isStreaming && msg.text.length === 0;
                                const messageSources = msg.citations?.length ? msg.citations : sources;

                                return (
                                    <div key={msg.id || `${msg.sender}-${String(msg.text).slice(0, 32)}`} className={`message-row ${isUser ? 'is-user' : 'is-bot'}`}>
                                        <div className={`message-bubble ${isUser ? 'user-bubble' : 'bot-bubble'} ${msg.error ? 'message-error' : ''}`}>
                                            {showTypingIndicator ? (
                                                <div className="typing-indicator" role="status" aria-label={t('chat.thinking')}>
                                                    <span></span><span></span><span></span>
                                                </div>
                                            ) : (
                                                <CitationMarkdown
                                                    text={msg.text}
                                                    sources={messageSources}
                                                    citationOrder={citationOrder}
                                                    onOpenCitation={handleOpenCitation}
                                                />
                                            )}
                                            {!isUser && msg.text && !isStreaming && msg.id !== 'welcome' && (
                                                <div className="message-actions">
                                                    {msg.error && msg.retryText && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link"
                                                            onClick={() => handlePolicySend(msg.retryText)}
                                                        >
                                                            <i className="bi bi-arrow-repeat" aria-hidden="true" />
                                                            <span>{t('chat.retry')}</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link"
                                                        onClick={() => handleCopyMessage(msg.text, index)}
                                                        aria-label={t('chat.copyResponse')}
                                                    >
                                                        <i className={`bi bi-${copiedMessageIndex === index ? 'check2' : 'copy'}`} aria-hidden="true" />
                                                        <span>{copiedMessageIndex === index ? t('chat.copied') : t('chat.copyResponse')}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <form className="chat-composer" onSubmit={event => { event.preventDefault(); handleSend(); }}>
                            <label htmlFor="policy-chat-input" className="visually-hidden">{t('chat.inputLabel')}</label>
                            <input
                                id="policy-chat-input"
                                name="query"
                                type="text"
                                className="form-control"
                                value={input}
                                onChange={event => setInput(event.target.value)}
                                placeholder={t('chat.placeholder')}
                                disabled={isLoading || isStreaming}
                                autoComplete="off"
                            />
                            {isLoading || isStreaming ? (
                                <button type="button" onClick={handleStop} className="btn btn-outline-danger composer-action" aria-label={t('chat.stop')}>
                                    <i className="bi bi-stop-fill" aria-hidden="true" />
                                    <span>{t('chat.stop')}</span>
                                </button>
                            ) : (
                                <button type="submit" disabled={!input.trim()} className="btn btn-primary composer-action" aria-label={t('chat.send')}>
                                    <span>{t('chat.send')}</span>
                                    <i className="bi bi-arrow-up" aria-hidden="true" />
                                </button>
                            )}
                        </form>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ChatWindow;
